<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Creditor Receipt</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ purchase?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/user-credit/' + purchase?.supplier?.id)" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <PrintButtonHeader
            :title="'Creditor-Invoice-' + purchase?.batch_code"
            :urlA4="'/creditor-receipt/'"
            :url58mm="'/print58mm-creditor-receipt/'"
            :url80mm="'/print80mm-creditor-receipt/'"
            :currentUrl="'View Creditor Receipt'"
            :data="creditor_credits"
            :type="'Creditor Receipt'"
            :loading="loading"
          />
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card mb-4">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
        <div class="skeleton skeleton--table mt-4"></div>
      </div>
  
      <div v-else class="page-grid">
  
        <!-- LEFT: Invoice -->
        <div class="invoice-panel">
          <Invoice
            :purchase_items="purchase_items"
            :purchase_array="purchase_array"
            :purchase="purchase"
            :creditor_credits="creditor_credits"
            :loading="loading"
          />
        </div>
  
        <!-- RIGHT: Record Payment -->
        <div class="receipt-panel">
          <div class="form-card">
            <div class="section-header">
              <div class="section-icon section-icon--purple">
                <font-awesome-icon icon="hand-holding-usd" />
              </div>
              <div>
                <p class="section-title">Record Payment</p>
                <p class="section-sub">Record a payment to this supplier</p>
              </div>
            </div>
  
            <!-- Outstanding balance chip -->
            <div class="outstanding-chip">
              <span class="outstanding-label">Outstanding Balance</span>
              <span class="outstanding-value">{{ $filters.formatMoney(def_balance) }}</span>
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Payment Type</label>
              <select class="field-input field-select" v-model="payment_type" :disabled="permission.create_record_payment != 1">
                <option value="" disabled selected>Select payment type</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
              </select>
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Payment Amount</label>
              <input
                type="text"
                class="field-input"
                v-model="amount"
                @keyup="handleAmount"
                :disabled="permission.create_record_payment != 1"
                placeholder="0.00"
                onkeypress="return /^[0-9.]+$/i.test(event.key)"
              />
            </div>
  
            <div class="field-group mt-3">
              <label class="field-label">Remaining Balance</label>
              <input disabled type="number" class="field-input field-input--readonly" v-model="balance" />
            </div>
  
            <button
              class="btn-primary-action w-full mt-4"
              @click.prevent="makePayment"
              :disabled="crudLoading || permission.create_record_payment != 1"
            >
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check" class="me-2" />
              {{ crudLoading ? 'Processing...' : 'Record Payment' }}
            </button>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import Invoice from '../purchase/components/invoice.vue';
  
  export default {
    components: { Invoice },
    mixins: [reusableMixin],
  
    data() {
      return {
        toast:            useToast(),
        store:            useStore(),
        creditor_credits: null,
        purchase:         null,
        creditor:         [],
        purchase_items:   [],
        purchase_array:   [],
        payment_type:     null,
        amount:           null,
        balance:          '0.00',
        def_balance:      '0.00',
        crudLoading:      false,
        loading:          true,
      };
    },
  
    created() { this.getCreditor(); },
  
    methods: {
      handleAmount() {
        const outstanding = this.creditor_credits?.total_amount - this.creditor_credits?.amount_paid;
        this.balance      = (outstanding - this.amount).toFixed(2);
        if (Math.sign(this.balance) === -1) {
          this.amount  = this.creditor_credits?.total_balance;
          this.balance = parseFloat(outstanding).toFixed(2);
        }
        if (this.amount == this.balance) this.balance = (0).toFixed(2);
      },
  
      getCreditor() {
        this.loading = true;
        axios.get(`/creditors/id/${this.$route.params.id}`)
          .then(response => {
            this.loading          = false;
            this.creditor_credits = response.data.data;
            this.creditor         = response.data.data.creditor;
            this.purchase         = this.creditor_credits?.purchase;
            this.purchase_items   = this.creditor_credits?.purchase?.purchase_item;
            this.purchase_array   = this.purchase_items.map(s => ({
              product_name:  s?.product?.product_name,
              cost_price:    s?.cost_price,
              variant_name:  s?.variant?.variant_name ?? s?.product?.unit_of_measurement,
              quantity:      s?.quantity,
              total:         parseFloat(s?.amount_paid),
            }));
            this.def_balance = (this.creditor_credits?.total_amount - this.creditor_credits?.amount_paid).toFixed(2);
            this.balance     = this.def_balance;
          })
          .catch(() => { this.loading = false; this.toast.error('Something went wrong'); });
      },
  
      makePayment() {
        if (!this.payment_type) { this.toast.error('Please select a payment type first'); return; }
        this.crudLoading = true;
        axios.post('/creditors/pay-credit', {
          amount:      this.amount,
          supplier_id: this.purchase?.supplier_id,
          credit_id:   this.creditor_credits?.id,
        }).then(response => {
          this.crudLoading = false;
          this.$router.push({ name: 'Creditor' });
          this.toast.success(response.data.message);
        }).catch(error => {
          this.crudLoading = false;
          this.toast.error(error.response?.data?.message?.[0] ?? error.response?.data?.message ?? 'Something went wrong');
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  
  /* ── Skeleton ── */
  .skeleton-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton       { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  .skeleton--table { height:220px; }
  .mt-4 { margin-top:16px; }
  
  /* ── Page Grid ── */
  .page-grid { display:grid; grid-template-columns:1fr 320px; gap:24px; align-items:start; }
  
  /* ── Invoice Panel ── */
  .invoice-panel {
    background:#fff; border:1px solid #e4eaf2;
    border-radius:12px; overflow:hidden;
    box-shadow:0 4px 24px rgba(0,0,0,.06);
  }
  
  /* ── Receipt Panel ── */
  .receipt-panel { position:sticky; top:20px; }
  .form-card     { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4          { margin-bottom:16px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Outstanding Chip ── */
  .outstanding-chip { display:flex; justify-content:space-between; align-items:center; background:#fff0f0; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:4px; }
  .outstanding-label { font-size:13px; font-weight:600; color:#e03535; }
  .outstanding-value { font-size:20px; font-weight:800; color:#e03535; }
  
  /* ── Fields ── */
  .field-group  { display:flex; flex-direction:column; gap:6px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus    { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f8fafc; color:#aab8cc; cursor:not-allowed; }
  .field-input--readonly { background:#f8fafc; }
  .field-select { appearance:auto; cursor:pointer; }
  .mt-3 { margin-top:12px; }
  
  /* ── Button ── */
  .btn-primary-action { display:inline-flex; align-items:center; justify-content:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .w-full { width:100%; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .page-grid { grid-template-columns:1fr; } .receipt-panel { position:static; } }
  @media(max-width:576px)  { .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  </style>